import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { MediaItem, TagFacet, VaultStatus, WallPlaceStatus, WallUploadItem } from "@gooner/shared";
import {
  FACET_ORDER,
  NEW_FACETS,
  VAULT_COLLECTION_ID,
  WALL_UPLOAD_TYPES,
  clipKey,
  facetLabel,
  facetOf,
  ownerScopedPlaylistId,
  tagLabel,
} from "@gooner/shared";
import { NewBadge } from "./NewBadge";
import { api, apiErrorOf, userApi } from "../lib/api";
import { useSession } from "../lib/session";
import { usePublicSettings } from "../lib/publicSettings";
import { ClipTile } from "../control/components/ClipTile";
import { keywordSourceOf } from "../control/components/FeedPicker";
import { useContentTags } from "../control/lib/feedData";
import { fmtBytes } from "../control/lib/format";
import { cx } from "../control/ui/primitives";

/**
 * THE MEDIA PICKER — the one way a member chooses a clip of their own to publish.
 *
 * Lifted out of the Community Wall's buy dialog (2026-09-13) when the Feed needed the
 * very same tray: your faps, each of your collections, your uploads, the keywords, the
 * catalog's most viewed. Two surfaces, one picker — a post's clip and a square's clips
 * come through identical doors, so nothing pickable here can be private or banned
 * (every shelf resolves through the visibility-safe paths a screen plays through), and
 * the one exception, the Upload tab, is the caller's own vault, which strangers see only
 * after a moderator clears each item.
 *
 * YOUR OWN SHELVES ARE THE PICKER. Free-text search was here first and was theatre:
 * library clips carry no titles, so `q` had nothing to match and the box mostly
 * returned silence. What somebody wants to publish is what they already curated.
 *
 * The picker owns the tray and the selection's SHAPE (`max`, the length rule); the
 * caller owns the selection itself (`picks`/`onChange`) and everything after it — the
 * wall its checkout, the Feed its post. Analytics stay the caller's too (`onTab`,
 * `onUploadStarted`), so the wall's events did not change names when the code moved.
 */

/** The picker's ceiling — a decision surface, not a browse surface. */
const PICKER_LIMIT = 60;

/** Until when the keyword groups added 2026-09-12 (NEW_FACETS) wear "New!" here — a
 *  month after launch. Per surface, not shared: see NewBadge's header. */
const GROUPS_NEW_UNTIL = Date.UTC(2026, 9, 13);

/**
 * One thing somebody has chosen: a catalog clip, or one of their own vault items. Both
 * travel as `clipKey(item)` — `web:<token>` and `upload:<token>` never collide — and a
 * caller splits them by the item's own sourceKind, because the server admits the two
 * through different doors (a catalog token through the general resolver, an upload
 * token through the owner-scoped one). `wallStatus` is an upload's standing in the
 * review queue, for the badge.
 */
export type MediaPick = { item: MediaItem; wallStatus?: WallPlaceStatus };

/** One spelling of "is this selected". */
export const pickId = (p: MediaPick): string => clipKey(p.item);
export const isUploadPick = (p: MediaPick): boolean => p.item.sourceKind === "upload";

export function MediaPicker({
  picks,
  onChange,
  max,
  maxSeconds,
  tooLongCopy = "Too long",
  uploadNote,
  onTab,
  onUploadStarted,
  allowUploads = true,
}: {
  picks: MediaPick[];
  onChange: (next: MediaPick[]) => void;
  /** How many may be picked. At 1 a new pick REPLACES the old one — a single-clip
   *  surface should not make somebody un-pick before they can pick. */
  max: number;
  /** When set, an uploaded VIDEO longer than this is shown but not pickable (the wall's
   *  ≤60s square). Absent means no length rule beyond the vault's own. */
  maxSeconds?: number;
  /** The badge on a too-long tile. */
  tooLongCopy?: string;
  /** What the Upload tab says under the file input, after the format line — the
   *  surface's own promise about where the file goes and who sees it when. */
  uploadNote?: ReactNode;
  /** A shelf was opened — `tab` is the key, collections bucketed to the word. */
  onTab?: (tab: string) => void;
  /** A file was chosen — the ATTEMPT, before the vault answers. */
  onUploadStarted?: (bytes: number) => void;
  /** When false, Upload / vault shelves are omitted (catalog-only surfaces). */
  allowUploads?: boolean;
}) {
  const user = useSession((s) => s.user);
  // The Upload tab exists only while BOTH switches are on: the wall's (may somebody
  // publish their own media) and the vault's (is there anywhere for the file to go).
  // Optimistic until the one settings fetch answers, so the tab does not blink in.
  // Catalog-only surfaces pass allowUploads={false} to forbid vault.
  const pub = usePublicSettings();
  const uploadsOn = allowUploads && (pub ? pub.wall.uploadsEnabled && pub.vault.enabled : true);
  // Whether a moderator stands between an upload and everyone else — the copy under the
  // file input must say which, before the file goes in. Unknown reads as reviewed: the
  // stricter promise is the safe one to make for a beat.
  const reviewed = pub ? pub.wall.reviewUploads : true;
  const uploadTab = uploadsOn ? [{ key: "upload", label: "Upload" }] : [];
  // "faps" | "upload" | "kw" | "top" | `pl:<playlistId>` — what the tray is showing.
  const [tab, setTab] = useState<string>("faps");
  const [tabs, setTabs] = useState<Array<{ key: string; label: string }>>([
    { key: "faps", label: "Your faps" },
    ...uploadTab,
    { key: "kw", label: "By keyword" },
    { key: "top", label: "Most viewed" },
  ]);
  // The keyword tab's picks — flat here, regrouped into OR-within-facet/AND-across by
  // keywordSourceOf, exactly the grouping the editor's picker produces.
  const [kwPicked, setKwPicked] = useState<string[]>([]);
  const [uploads, setUploads] = useState<WallUploadItem[]>([]);
  const [vault, setVault] = useState<VaultStatus | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { tags: keywordTags } = useContentTags(tab === "kw");
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loadingClips, setLoadingClips] = useState(true);
  // The no-faps bounce to Most viewed happens ONCE — a deliberate later tap on the
  // faps chip should show its (empty) truth, not bounce back.
  const autoSwitched = useRef(false);

  /** The length rule, when the caller has one. */
  const tooLong = (p: MediaPick): boolean =>
    maxSeconds !== undefined && isUploadPick(p) && p.item.kind === "video" && (p.item.durationSec ?? 0) > maxSeconds + 0.5;

  useEffect(() => {
    let cancelled = false;
    void userApi
      .listPlaylists()
      .then((r) => {
        if (cancelled) return;
        setTabs([
          { key: "faps", label: "Your faps" },
          // The two virtual collections ride listPlaylists too. Faps is dropped because
          // the "Your faps" tab already covers it with the owner-scoped id; Your Uploads
          // because the Upload tab IS the vault, with the review status on each tile —
          // offering it as a plain shelf would route the same items through the catalog
          // door, which refuses them.
          ...r.playlists
            .filter((p) => p.id !== "faps" && p.id !== VAULT_COLLECTION_ID)
            .map((p) => ({ key: `pl:${p.id}`, label: p.name })),
          // Rebuilt in BOTH places or the tab vanishes the moment playlists load.
          ...uploadTab,
          { key: "kw", label: "By keyword" },
          { key: "top", label: "Most viewed" },
        ]);
      })
      .catch(() => {
        /* signed-in but the list failed: the built-in tabs still work */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, uploadsOn]);

  useEffect(() => {
    let cancelled = false;
    setLoadingClips(true);
    const load = async (): Promise<MediaItem[]> => {
      // The Upload tab draws from the member's own vault, not from a source.
      if (tab === "upload") return [];
      if (tab === "top") {
        return (await api.searchCatalog({ limit: PICKER_LIMIT, sort: "views" })).items;
      }
      if (tab === "kw") {
        // The keywords source adapter — the same clips a region playing these
        // keywords would get, visibility-safe and banned-stripped server-side.
        if (kwPicked.length === 0) return [];
        return (await api.fetchSource(keywordSourceOf(kwPicked))).items;
      }
      const playlistId = tab === "faps" ? ownerScopedPlaylistId("faps", user?.id) : tab.slice(3);
      if (!playlistId) return [];
      return (await api.fetchSource({ kind: "playlist", playlistId })).items;
    };
    void load()
      .then((list) => {
        if (cancelled) return;
        const vids = list.filter((m) => m.kind === "video").slice(0, PICKER_LIMIT);
        if (tab === "faps" && vids.length === 0 && !autoSwitched.current) {
          autoSwitched.current = true;
          setTab("top");
          return;
        }
        setItems(vids);
        setLoadingClips(false);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setLoadingClips(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tab, user, kwPicked]);

  /** What the grid shows for the current tab, as picks. The Upload tab is the vault,
   *  each item carrying its review status for the badge. */
  const tray = useMemo<MediaPick[]>(
    () =>
      tab === "upload"
        ? uploads.map((u) => ({ item: u.item, wallStatus: u.status }))
        : items.map((m) => ({ item: m })),
    [tab, uploads, items],
  );
  const loadUploads = useCallback(() => {
    userApi
      .wallUploads()
      .then((r) => setUploads(r.uploads))
      .catch(() => setUploads([]));
    // The vault's own numbers, for the copy under the picker — quoted, never a literal.
    userApi
      .vaultStatus()
      .then(setVault)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (tab === "upload") loadUploads();
  }, [tab, loadUploads]);

  const pickedIds = useMemo(() => new Set(picks.map(pickId)), [picks]);
  /** Add, or at the ceiling: replace when the ceiling is one, else nothing. */
  const add = (p: MediaPick): MediaPick[] | null => {
    const id = pickId(p);
    if (picks.some((c) => pickId(c) === id)) return null;
    if (picks.length >= max) return max === 1 ? [p] : null;
    return [...picks, p];
  };
  const toggle = (p: MediaPick) => {
    if (tooLong(p)) return;
    const id = pickId(p);
    if (picks.some((c) => pickId(c) === id)) {
      onChange(picks.filter((c) => pickId(c) !== id));
      return;
    }
    const next = add(p);
    if (next) onChange(next);
  };

  /*
   * A file picked here goes into the VAULT — the same POST /api/uploads Your Uploads
   * uses, with the vault's rules and quota — and then lands in this tray, selected.
   * Neither surface has an intake of its own; what each adds is the review, minted when
   * the square is bought or the post is made.
   */
  const sendFile = async (file: File) => {
    setUploadError(null);
    if (max > 1 && picks.length >= max) {
      // toggle() is a silent no-op when the tray is full, which on THIS path would look
      // like the upload silently failed. Say it instead.
      setUploadError(`You've already picked ${max} — remove one first.`);
      return;
    }
    setUploading(true);
    try {
      onUploadStarted?.(file.size);
      const r = await userApi.vaultUpload(file);
      setVault(r.vault);
      const id = clipKey(r.item);
      // Newest first; the same bytes again (`existing`) move to the front rather than
      // appearing twice.
      setUploads((cur) => [
        { id, status: cur.find((u) => u.id === id)?.status ?? "none", item: r.item, createdAt: Date.now() },
        ...cur.filter((u) => u.id !== id),
      ]);
      // Selected on arrival: they chose this file a moment ago, and making them click it
      // again is asking them to confirm something they already said.
      const pick: MediaPick = { item: r.item, wallStatus: "none" };
      if (tooLong(pick)) {
        setUploadError(`Saved to Your Uploads — but this plays clips up to ${maxSeconds}s, so it can't go here.`);
        return;
      }
      const next = add(pick);
      if (next) onChange(next);
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "";
      // The vault's typed refusal: the fix is room, not a retry, and the room is bought
      // on the Your Uploads page.
      setUploadError(apiErrorOf(err)?.vaultFull ? "Your vault is full — make room or expand it in Your Uploads." : msg || "That upload didn't go through.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      {/* The shelves: faps, each collection, uploads, keywords, most viewed. */}
      <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTab(t.key);
              onTab?.(t.key.startsWith("pl:") ? "collection" : t.key);
            }}
            className={cx(
              "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
              tab === t.key
                ? "bg-[var(--color-accent)] text-black"
                : "bg-[var(--color-panel-2)] text-zinc-300 hover:text-white",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "kw" ? (
        <div className="mb-2 max-h-64 overflow-y-auto rounded-lg border border-[var(--color-edge)] p-2">
          {keywordFacetGroups(keywordTags).map(([facet, list]) => (
            <div key={facet} className="mb-1.5 flex flex-wrap items-center gap-1 last:mb-0">
              <span className="mr-1 text-[10px] uppercase tracking-wide text-zinc-500">
                {facet === "other" ? "More" : facetLabel(facet as TagFacet)}
                {(NEW_FACETS as readonly string[]).includes(facet) && Date.now() < GROUPS_NEW_UNTIL ? (
                  <NewBadge variant="inline" />
                ) : null}
              </span>
              {list.map((t) => {
                const on = kwPicked.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() =>
                      setKwPicked((cur) => (on ? cur.filter((k) => k !== t) : [...cur, t]))
                    }
                    className={cx(
                      "rounded-full px-2 py-0.5 text-[11px]",
                      on
                        ? "bg-[var(--color-accent)] text-black"
                        : "bg-[var(--color-panel-2)] text-zinc-300 hover:text-white",
                    )}
                  >
                    {tagLabel(t)}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
      {tab === "upload" ? (
        <div className="mb-2 rounded-lg border border-[var(--color-edge)] p-3">
          <input
            ref={fileRef}
            type="file"
            accept={Object.keys(WALL_UPLOAD_TYPES).join(",")}
            disabled={uploading}
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) void sendFile(f);
            }}
            className="block w-full text-xs text-zinc-400 file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--color-accent)] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-black hover:file:opacity-90"
          />
          <p className="mt-2 text-[11px] leading-snug text-zinc-500">
            MP4 or WebM video, or a JPG, PNG or WebP image
            {vault ? `, under ${fmtBytes(vault.maxFileBytes)}` : ""}.
            {maxSeconds !== undefined ? ` This plays clips up to ${maxSeconds}s.` : ""} It has to be
            porn or gooning related.
          </p>
          {/* The things somebody must know BEFORE publishing, not after: the wait, and
              where the file lives. The surface says the rest. */}
          <p className="mt-1 text-[11px] leading-snug text-zinc-500">
            Uploading says you're 18+, that this is yours to post, and that you hold any
            required consents and records. Files land in Your Uploads
            {reviewed
              ? ", and a moderator clears each one before anyone else sees it"
              : ", and what you put up is live the moment you post it — a moderator can still pull it, and pulling ends the account"}
            . Uploads are never added to the catalog.{reviewed && uploadNote ? <> {uploadNote}</> : null}
          </p>
          {uploading ? (
            <p className="mt-2 text-xs text-zinc-400">Sending…</p>
          ) : uploadError ? (
            <p className="mt-2 text-xs text-red-400">{uploadError}</p>
          ) : null}
        </div>
      ) : null}
      <div className="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
        {tray.map((p) => {
          const on = pickedIds.has(pickId(p));
          const long = tooLong(p);
          return (
            <div
              key={pickId(p)}
              className={cx(
                "rounded-lg ring-2 ring-inset",
                on ? "ring-[var(--color-accent)]" : "ring-transparent",
                long && "opacity-50",
              )}
            >
              <ClipTile
                clip={p.item}
                onClick={() => toggle(p)}
                badge={
                  on ? (
                    <span className="rounded bg-[var(--color-accent)] px-1.5 py-0.5 text-[11px] font-semibold text-black">
                      ✓
                    </span>
                  ) : long ? (
                    // The vault takes longer files than this surface plays; say so on
                    // the tile rather than let the server be the one to refuse it.
                    <span className="rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-zinc-300">
                      {tooLongCopy}
                    </span>
                  ) : p.wallStatus === "pending" ? (
                    // A pending upload says so on its own tile: it is pickable, and it
                    // simply does not appear to anyone else until a moderator clears it.
                    <span className="rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                      In review
                    </span>
                  ) : p.wallStatus === "live" ? (
                    // Already cleared once: publishing it again needs no second review.
                    <span className="rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
                      Cleared
                    </span>
                  ) : null
                }
              />
            </div>
          );
        })}
      </div>
      {tray.length === 0 ? (
        <p className="py-6 text-center text-sm text-zinc-500">
          {tab === "upload"
            ? "Nothing in Your Uploads yet — pick a file above."
            : tab === "kw" && kwPicked.length === 0
              ? "Pick keywords above to fill the tray."
            : loadingClips
              ? "Loading clips…"
              : tab === "faps"
                ? "No faps yet — crown clips while you goon and they land here."
                : "Nothing to pick from here."}
        </p>
      ) : null}
    </>
  );
}

/** The tag vocabulary grouped for the chip cloud: facets in FACET_ORDER, free tags last —
 *  the same order and labels the browse sidebar and the editor's picker use. */
function keywordFacetGroups(tags: string[]): Array<[string, string[]]> {
  const by = new Map<string, string[]>();
  for (const t of tags) {
    const f = facetOf(t) ?? "other";
    const list = by.get(f);
    if (list) list.push(t);
    else by.set(f, [t]);
  }
  const out: Array<[string, string[]]> = [];
  for (const f of FACET_ORDER) if (by.has(f)) out.push([f, by.get(f)!]);
  if (by.has("other")) out.push(["other", by.get("other")!]);
  return out;
}
